import { Actions, ofType } from '@ngrx/effects';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, Observable, Subscription, take, tap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { AddTagsComponent } from '../add-tags/add-tags.component';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { DeleteTag, DeleteTagSuccess, FetchTags } from '../../store/tags.actions';
import { ITag } from '../../models/tags.models';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectionController } from 'src/app/shared/utils/selection-controller';

@Component({
  selector: 'app-list-tags',
  templateUrl: './list-tags.component.html',
  styleUrls: ['./list-tags.component.scss']
})
export class ListTagsComponent implements OnInit, OnDestroy {
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  }
  tags$: Observable<ITag[]> = this.store.select((state) => state.tags.tags);
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.tags.metadata);
  key: string | undefined = undefined;
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);

  searchControl = new FormControl('');

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ITag>((item) => item.id);

  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
  }

  ngOnInit(): void {
    this.searchControl.setValue(this.key || '', { emitEvent: false });

    const sub1 = this.store.select('tags', 'keyword')
      .pipe(
        distinctUntilChanged(),
        tap(keyword => {
          this.key = keyword;
          this.toPage(this.page);
        })
      )
      .subscribe();

    const sub2 = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        const next = (value ?? '').toString();
        if (next === (this.key ?? '')) return;
        this.key = next;
        this.page = { ...this.page, number: 1 };
        this.toPage(this.page);
      });

    // Deferred so the selection state settles outside the change-detection pass
    // that delivered the new rows, which would otherwise trip ExpressionChanged.
    const sub3 = this.tags$.subscribe(list => {
      Promise.resolve().then(() => this.selection.setRows(list));
    });

    this.subscriptions.push(sub1, sub2, sub3);
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(FetchTags({ page: this.page, key: this.key }));
    const filter: any = {};
    if (this.key && this.key !== '') {
      filter.keyword = this.key;
    }
    const queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  openTag(tag?: ITag) {
    this.dialog.open(AddTagsComponent, {
      data: tag,
      disableClose: true,
    });
  }

  // ── Selection ────────────────────────────────────────────────────────────

  bulkDelete(): void {
    if (!this.selection.hasSelection) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) return;
      const ids = this.selection.ids;
      this.selection.clear();
      const refreshSub = this.actions$.pipe(
        ofType(DeleteTagSuccess),
        take(ids.length)
      ).subscribe(() => {
        refreshSub.unsubscribe();
        this.toPage(this.page);
      });
      ids.forEach(id => this.store.dispatch(DeleteTag({ id })));
    });
  }

  delete(tag: ITag) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const deleteSuccessSub = this.actions$.pipe(
          ofType(DeleteTagSuccess),
          take(1)
        ).subscribe(() => {
          this.toPage(this.page);
          deleteSuccessSub.unsubscribe();
        });

        this.store.dispatch(DeleteTag({ id: tag.id }));
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()));
  }

}
